/**
 * QUEUEING A COMPLETED FORM — and the contract mismatch this file makes visible.
 * ============================================================================
 *
 * `src/forms/` produces a {@link SubmissionPayload}: a provenance-bearing record of what the
 * technician asserted, with `answers` as an ARRAY of `AnswerRecord`s. `src/core/sync.ts` sends a
 * {@link SubmissionOutboxPayload}: `recordType`, `capturedAt`, `photoBlobIds`, and `answers` as a
 * **FLAT MAP OF SCALARS** (a nested object or array is the mass-assignment shape T-11 and the server
 * refuses it with `answers-not-flat`).
 *
 * Those two shapes are not the same, and neither module is wrong. `src/forms/submit-core.ts`'s
 * `submitToOutbox` enqueues the forms shape verbatim, which the flusher would then read as
 * `payload.recordType === undefined` and `payload.answers` as an array. So the composition needs a
 * translation, and this file is it. Nothing in either subsystem changes.
 *
 * The queued row is the outbox shape PLUS a `form` field carrying the whole provenance payload. The
 * flusher reads only the fields it names, so the extra key costs nothing on the wire and means the
 * device keeps the full "who asserted what" record for the review screen and for support.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ⚠️ **AN UNRESOLVED CONTRACT GAP, RECORDED RATHER THAN PAPERED OVER.**
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * `infra/src/domain/form-schema.ts` (read 2026-09-03) accepts a SMALL, FIXED set of answer ids —
 * `vendorReference`, `comments`, `anchorType`, `indoorOutdoor`, `moduleSerial1…20`,
 * `moduleSerialPhoto1…20` — and **rejects any unrecognised id with a 400 naming it**, deliberately,
 * because *"unknown answers are REJECTED, never dropped."* The PWA schemas in `src/forms/schemas/`
 * use their own ids (`module01_serial`, `photo_front`, `intervention_type`, …), which are not that
 * set. Likewise `recordType`: the server's seven active `Work_Submission__c` record types include
 * `Site Prep`, but `SITE_PREP.destination.recordTypeLabel` is `Site Prep Supplier Form`, and
 * `POST_INSTALL` targets `Sim_Ticket__c`, which is not a `Work_Submission__c` record type at all.
 *
 * **So a submission queued today would be rejected by the intake handler on the id set.** I have NOT
 * invented a translation table: building one means either duplicating the server's list into this
 * package (a second source of truth that drifts silently — the precise failure mode this repo keeps
 * finding) or making up per-question mappings, and both are worse than an honest gap. It is also not
 * currently reachable: `core/auth.ts` is a stub, so `flushOnce` returns `skipped:
 * 'auth-not-configured'` and nothing is sent at all.
 *
 * What this file does instead: keys `answers` by the PWA field id, which is the only id the client
 * legitimately owns, and leaves the gap stated here and in the report. Closing it is one table, on
 * one side of the wire, with evidence — not a guess made at composition time.
 */

import { enqueue } from '@/core/outbox';
import { KIND_SUBMISSION, type SubmissionOutboxPayload } from '@/core/sync';
import { SUBMISSION_KIND, type SubmissionPayload } from '@/forms';
import type { KioskContext } from './flow';

/**
 * The row that goes on the queue.
 *
 * `form` is additive: `sync.ts` reads `recordType`, `idempotencyKey` (off the row, not the payload),
 * `answers`, `kioskId`, `ticketId`, `capturedAt` and `photoBlobIds`, and ignores everything else.
 */
export interface QueuedSubmission extends SubmissionOutboxPayload {
  readonly form: SubmissionPayload;
  /** Field ids whose answer could not be reduced to a scalar. Never silently empty — see below. */
  readonly notFlattened: readonly string[];
}

export type FlatAnswers = Readonly<Record<string, string | number | boolean | null>>;

/**
 * Reduce the provenance-bearing answers to the flat scalar map the intake handler requires.
 *
 * The four non-scalar cases, and what happens to each:
 *
 *  · **multi-select** (`readonly string[]`) → joined with `; `. That is Salesforce's own multipicklist
 *    separator, so the value survives into a multipicklist field without a second transformation.
 *  · **serial** (`SerialValue`) → the `serial` string only. `method`, `format` and `raw` are the
 *    evidence that distinguishes a scan from a typed entry, and they are **kept on the row** in
 *    `form`, not thrown away — but they are not smuggled into the flat map as invented
 *    `<fieldId>__method` keys, because the server rejects ids it does not know and inventing one
 *    would fail the whole submission.
 *  · **photo** (`readonly PhotoRef[]`) → not an answer. Photos travel as `photoBlobIds` and are
 *    uploaded as separate outbox items after the submission is accepted (see `sync.ts`), so putting a
 *    blob id in `answers` would be a client-asserted photo key, which C-37 forbids.
 *  · **anything else** → listed in `notFlattened`. It is returned rather than dropped so a caller
 *    (and the queued row) can see that something was left out. A silent drop here is the 26.9% failure.
 */
export function flattenAnswers(payload: SubmissionPayload): {
  answers: FlatAnswers;
  notFlattened: readonly string[];
} {
  const answers: Record<string, string | number | boolean | null> = {};
  const notFlattened: string[] = [];

  for (const record of payload.answers) {
    const value = record.value;

    if (record.kind === 'photo') {
      // Not a loss: the blob ids are on the row under `photoBlobIds`.
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      answers[record.fieldId] = value;
      continue;
    }
    if (value === null) {
      // `buildSubmission` omits unanswered fields, so a null here means the technician entered an
      // explicit empty. Preserved as null rather than skipped, so the two stay distinguishable.
      answers[record.fieldId] = null;
      continue;
    }
    if (Array.isArray(value)) {
      const strings = value.filter((item): item is string => typeof item === 'string');
      if (strings.length === value.length) {
        answers[record.fieldId] = strings.join('; ');
      } else {
        notFlattened.push(record.fieldId);
      }
      continue;
    }
    if (typeof value === 'object' && 'serial' in value && typeof value.serial === 'string') {
      answers[record.fieldId] = value.serial;
      continue;
    }
    notFlattened.push(record.fieldId);
  }

  return { answers, notFlattened };
}

/**
 * `recordType` for the intake body.
 *
 * `recordTypeLabel` when the schema declares one, else the object name. See the header: for two of
 * the five schemas neither string is one of the server's seven active record types, and that is a
 * known open gap rather than something this function should paper over by guessing a substitute.
 */
export function recordTypeFor(payload: SubmissionPayload): string {
  return payload.destination.recordTypeLabel ?? payload.destination.object;
}

export function toQueuedSubmission(payload: SubmissionPayload, kiosk: KioskContext): QueuedSubmission {
  const { answers, notFlattened } = flattenAnswers(payload);
  return {
    recordType: recordTypeFor(payload),
    kioskId: kiosk.kioskId,
    // `capturedAt` is a CLAIM about when the work happened, from the device clock, and the server
    // stores it as such (AC-3). `receivedAt` is the server's and is never sent.
    capturedAt: new Date(payload.completedAt).toISOString(),
    answers,
    photoBlobIds: payload.photoBlobIds,
    form: payload,
    notFlattened,
  };
}

/**
 * Queue a completed form. **This is the whole submit path, and it never touches the network.**
 *
 * `enqueue` resolves only after the IndexedDB transaction commits, so by the time this returns the
 * submission survives the tab being killed, the device rebooting, and being offline for days. There is
 * no online branch to test, because there is no online branch.
 *
 * The two `kind` constants are asserted equal rather than assumed: `src/forms` and `src/core/sync`
 * each declare one, and if they ever drift the flusher would classify every submission as an unknown
 * kind and mark it `failed`. Cheap check, expensive failure.
 */
export async function enqueueSubmission(
  payload: SubmissionPayload,
  kiosk: KioskContext,
): Promise<{ id: string; idempotencyKey: string }> {
  if (SUBMISSION_KIND !== KIND_SUBMISSION) {
    throw new Error(
      `outbox kind mismatch: src/forms says "${SUBMISSION_KIND}", src/core/sync says "${KIND_SUBMISSION}". ` +
        'The flusher dispatches on this string; a mismatch would fail every submission.',
    );
  }
  const item = await enqueue<QueuedSubmission>(KIND_SUBMISSION, toQueuedSubmission(payload, kiosk));
  return { id: item.id, idempotencyKey: item.idempotencyKey };
}
